import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Clapperboard, Download, Film, Image as ImageIcon, Link2, Pause, Play, Share2 } from "lucide-react";
import { toast } from "sonner";
import { useDarkMode } from "../hooks/useDarkMode";
import usePulseLocale from "./i18n";
import { tokens, fmtCompact, fmtNum } from "./theme";
import { SkeletonCard, NoData, SourceChip } from "./components";
import PulseLayout from "./PulseLayout";
import { pulseGet } from "./api";
import { CityBubbleChart, CityRankedBars, BUCKET_COLORS } from "./CityBubbleChart";
import { exportChartPng } from "./exportChartPng";
import { CityComparePanel } from "./CityComparePanel";
import { exportTimelineGif } from "./exportChartGif";
import { exportTimelineVideo, videoExportSupported } from "./exportChartVideo";

const TOPS = [20, 30, 50];
const DEFAULT_CMP = { a: "IT001C", b: "FR001C" };
const CODE_RE = /^[A-Z]{2}\d{3}[CK]\d*$/;
const codeParam = (v) => { const c = (v || "").toUpperCase(); return CODE_RE.test(c) ? c : null; };

const STEP_MS = 1600;

const useIsNarrow = (bp = 640) => {
  const [narrow, setNarrow] = useState(typeof window !== "undefined" && window.innerWidth < bp);
  useEffect(() => {
    const on = () => setNarrow(window.innerWidth < bp);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, [bp]);
  return narrow;
};

const Yoy = ({ pct, dm, prev, t }) => {
  const T = tokens(dm);
  const { lang } = usePulseLocale();
  if (pct === null || pct === undefined) return <span style={{ color: T.muted }}>—</span>;
  return (
    <span className="font-semibold tabular-nums" style={{ color: pct >= 0 ? T.up : T.down }} title={prev ? t("cities_tt_yoy", { prev }) : undefined}>
      {pct >= 0 ? "▲" : "▼"} {fmtNum(Math.abs(pct), lang, 1)}%
    </span>
  );
};

const CityTooltip = ({ tip, dm }) => {
  const { t, lang } = usePulseLocale();
  const T = tokens(dm);
  if (!tip) return null;
  const { row: r, x, y } = tip;
  return (
    <div className="absolute z-20 pointer-events-none text-xs p-3 shadow-lg" data-testid="cities-tooltip"
      style={{ left: Math.min(x + 14, (tip.w || 900) - 240), top: y + 14, width: 230, background: T.surface, border: `1px solid ${T.border}`, color: T.text }}>
      <div className="flex items-center gap-2 mb-1.5">
        <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: BUCKET_COLORS[r.bucket] }} aria-hidden="true" />
        <span className="font-semibold">{r.name}</span>
        <span style={{ color: T.muted }}>· {r.country_iso3}</span>
        {r.greater_city && <span className="text-[9px] uppercase tracking-wider px-1" style={{ background: T.surface2, color: T.muted }}>{t("cities_greater")}</span>}
      </div>
      <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-0.5 tabular-nums">
        <dt style={{ color: T.muted }}>{t("cities_tt_rank")}</dt><dd className="text-right">#{r.rank}{r.rank_prev ? <span style={{ color: T.muted }}> ({r.prev_year}: #{r.rank_prev})</span> : null}</dd>
        <dt style={{ color: T.muted }}>{t("cities_tt_nights")}</dt><dd className="text-right">{fmtCompact(r.nights_total, lang)}</dd>
        {r.yoy_nights_pct !== null && r.yoy_nights_pct !== undefined && (
          <><dt style={{ color: T.muted }}>{t("cities_tt_yoy", { prev: r.prev_year })}</dt><dd className="text-right"><Yoy pct={r.yoy_nights_pct} dm={dm} t={t} /></dd></>
        )}
        <dt style={{ color: T.muted }}>{t("cities_tt_foreign")}</dt><dd className="text-right">{fmtNum(r.foreign_share_pct, lang, 1)}%</dd>
        <dt style={{ color: T.muted }}>{t("cities_tt_pop", { year: r.population_year })}</dt><dd className="text-right">{fmtCompact(r.population, lang)}</dd>
        <dt style={{ color: T.muted }}>{t("cities_tt_npr")}</dt><dd className="text-right font-semibold">{fmtNum(r.nights_per_resident, lang, 1)}</dd>
      </dl>
    </div>
  );
};

const CityTable = ({ rows, caption, testid, dm, showRank = true }) => {
  const { t, lang } = usePulseLocale();
  const T = tokens(dm);
  const th = "text-left text-[10px] font-bold uppercase tracking-wider py-2 px-2 whitespace-nowrap";
  return (
    <div className="overflow-x-auto" style={{ border: `1px solid ${T.border}` }}>
      <table className="w-full text-xs" data-testid={testid}>
        <caption className="sr-only">{caption}</caption>
        <thead style={{ background: T.surface2, color: T.muted }}>
          <tr>
            {showRank && <th className={th}>{t("cities_col_rank")}</th>}
            <th className={th}>{t("cities_col_city")}</th><th className={th}>{t("cities_col_country")}</th>
            <th className={`${th} text-right`}>{t("cities_col_nights")}</th><th className={`${th} text-right`}>{t("cities_col_yoy")}</th>
            <th className={`${th} text-right`}>{t("cities_col_foreign")}</th>
            <th className={`${th} text-right`}>{t("cities_col_pop")}</th><th className={`${th} text-right`}>{t("cities_col_pop_year")}</th>
            <th className={`${th} text-right`}>{t("cities_col_npr")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.city} style={{ borderTop: `1px solid ${T.border}` }} data-testid={`${testid}-row-${r.city}`}>
              {showRank && <td className="py-1.5 px-2 tabular-nums" style={{ color: T.muted }}>{r.rank}</td>}
              <td className="py-1.5 px-2 font-semibold whitespace-nowrap">
                <span className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle" style={{ background: BUCKET_COLORS[r.bucket] }} aria-hidden="true" />
                {r.name}{r.greater_city && <span className="ml-1 text-[9px] uppercase" style={{ color: T.muted }}>GC</span>}
              </td>
              <td className="py-1.5 px-2" style={{ color: T.muted }}>{r.country_iso3}</td>
              <td className="py-1.5 px-2 text-right tabular-nums">{fmtNum(r.nights_total / 1e6, lang, 1)}</td>
              <td className="py-1.5 px-2 text-right"><Yoy pct={r.yoy_nights_pct} prev={r.prev_year} dm={dm} t={t} /></td>
              <td className="py-1.5 px-2 text-right tabular-nums">{fmtNum(r.foreign_share_pct, lang, 1)}</td>
              <td className="py-1.5 px-2 text-right tabular-nums">{fmtNum(r.population, lang)}</td>
              <td className="py-1.5 px-2 text-right tabular-nums" style={{ color: T.muted }}>{r.population_year}</td>
              <td className="py-1.5 px-2 text-right tabular-nums font-semibold">{fmtNum(r.nights_per_resident, lang, 1)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/** Year-by-year timeline: play/pause + scrubber. `years` ascending, `year` current. */
const Timeline = ({ years, year, playing, onPlay, onYear, dm, t, top }) => {
  const T = tokens(dm);
  const idx = Math.max(0, years.indexOf(year));
  return (
    <div className="flex flex-col gap-2 mb-5 p-3" style={{ background: T.surface, border: `1px solid ${T.border}` }} data-testid="cities-timeline">
      <div className="flex items-center gap-3 flex-wrap">
        <button type="button" onClick={onPlay} data-testid="cities-play" aria-pressed={playing} aria-label={playing ? t("cities_pause") : t("cities_play")}
          className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-1.5"
          style={{ background: T.brand, color: "#fff", borderRadius: 2 }}>
          {playing ? <Pause className="w-3.5 h-3.5" aria-hidden="true" /> : <Play className="w-3.5 h-3.5" aria-hidden="true" />}
          {playing ? t("cities_pause") : t("cities_play")}
        </button>
        <span className="text-xs font-semibold" style={{ color: T.muted }} data-testid="cities-timeline-label">
          {t("cities_timeline", { from: years[0], to: years[years.length - 1] })}
        </span>
        <input type="range" min={0} max={years.length - 1} step={1} value={idx} data-testid="cities-year-slider"
          onChange={(e) => onYear(years[parseInt(e.target.value, 10)])} aria-valuetext={year}
          className="flex-1 min-w-[160px] accent-current" style={{ color: T.brand }} />
        <span className="pulse-serif text-3xl font-bold tabular-nums leading-none" style={{ color: T.text, minWidth: 80, textAlign: "right" }} data-testid="cities-timeline-year" aria-live="polite">{year}</span>
      </div>
      <div className="flex justify-between text-[10px] tabular-nums" style={{ color: T.muted }} aria-hidden="true">
        {years.map((y) => <span key={y}>{y}</span>)}
      </div>
      <p className="text-[11px]" style={{ color: T.muted }}>{t("cities_timeline_hint", { n: top })}</p>
    </div>
  );
};

export default function CitiesPage() {
  const [dm] = useDarkMode();
  const { t, lang } = usePulseLocale();
  const T = tokens(dm);
  const narrow = useIsNarrow();
  const [sp, setSp] = useSearchParams();
  const [year, setYear] = useState(() => (/^\d{4}$/.test(sp.get("year") || "") ? sp.get("year") : null));
  const [top, setTop] = useState(() => (TOPS.includes(parseInt(sp.get("top"), 10)) ? parseInt(sp.get("top"), 10) : 30));
  const [highlightIt, setHighlightIt] = useState(() => sp.get("it") !== "0");
  const [cmp, setCmp] = useState(() => ({ a: codeParam(sp.get("a")) || (sp.has("a") ? null : DEFAULT_CMP.a), b: codeParam(sp.get("b")) || (sp.has("b") ? null : DEFAULT_CMP.b) }));
  const [catalog, setCatalog] = useState([]);
  const [hist, setHist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tip, setTip] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [gifBusy, setGifBusy] = useState(false);
  const [mp4Busy, setMp4Busy] = useState(false);
  const svgRef = useRef(null);

  useEffect(() => {
    document.title = `${t("nav_cities")} — Pulse by TourismIntel`;
  }, [t]);

  useEffect(() => {
    let on = true;
    setLoading(true); setError(null); setPlaying(false);
    pulseGet("cities/history", { top, country: "IT", since: "2019" })
      .then((d) => { if (on) { setHist(d); setYear((y) => (y && d.years.includes(y) ? y : d.latest)); } })
      .catch((e) => { if (on) { setHist(null); setError(e?.response?.status || "error"); } })
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [top]);

  useEffect(() => {
    let on = true;
    pulseGet("cities/catalog").then((d) => on && setCatalog(d.cities || [])).catch(() => {});
    return () => { on = false; };
  }, []);

  // shareable URL: year (when not the latest), top (when not 30), it=0, a/b (when not the defaults); keeps ?lang
  useEffect(() => {
    if (!hist) return;
    setSp((prev) => {
      const next = new URLSearchParams(prev);
      const put = (k, v) => (v === null || v === undefined ? next.delete(k) : next.set(k, v));
      put("year", year && year !== hist.latest ? year : null);
      put("top", top !== 30 ? String(top) : null);
      put("it", highlightIt ? null : "0");
      put("a", cmp.a === DEFAULT_CMP.a && cmp.b === DEFAULT_CMP.b ? null : (cmp.a || ""));
      put("b", cmp.a === DEFAULT_CMP.a && cmp.b === DEFAULT_CMP.b ? null : (cmp.b || ""));
      return next.toString() === prev.toString() ? prev : next;
    }, { replace: true });
  }, [year, top, highlightIt, cmp, hist, setSp]);

  const years = useMemo(() => hist?.years || [], [hist]);
  const frame = useMemo(() => hist?.frames?.find((f) => f.year === year) || null, [hist, year]);
  // stable grid across frames only while travelling in time; the latest year at rest uses its own tight domain
  const timeTravel = playing || gifBusy || (year && hist?.latest && year !== hist.latest);
  const domainRows = useMemo(() => (timeTravel ? (hist?.frames || []).flatMap((f) => f.rows) : null), [hist, timeTravel]);
  const rows = frame?.rows || [];
  const yearLabel = frame?.year || year || "";
  const italy = useMemo(() => (frame?.country_rows || []).slice(0, 15), [frame]);
  const excluded = (frame?.excluded?.no_foreign_split || 0) + (frame?.excluded?.no_population || 0);
  const missing = frame?.missing_from_latest_top || [];

  // playback: step through the years; a separate effect stops at the last one
  useEffect(() => {
    if (!playing || years.length < 2) return undefined;
    const id = setInterval(() => {
      setYear((y) => years[Math.min(years.length - 1, Math.max(0, years.indexOf(y)) + 1)]);
    }, STEP_MS);
    return () => clearInterval(id);
  }, [playing, years]);
  useEffect(() => {
    if (playing && years.length && year === years[years.length - 1]) setPlaying(false);
  }, [playing, year, years]);

  const onPlay = useCallback(() => {
    if (!playing && years.length > 1 && year === years[years.length - 1]) setYear(years[0]);
    setPlaying((p) => !p);
    setTip(null);
  }, [playing, years, year]);

  const onSelect = (p) => {
    if (!p || p.city === cmp.a || p.city === cmp.b) return;
    const slot = !cmp.a ? "a" : "b";
    setCmp((c) => ({ ...c, [slot]: p.city }));
    toast.success(t(`cities_added_${slot}`, { name: p.name }));
  };

  const [shareOpen, setShareOpen] = useState(false);
  const socialUrl = () => `${window.location.origin}/api/s/pulse/cities${window.location.search}`;
  const copySocial = async () => {
    setShareOpen(false);
    try {
      await navigator.clipboard.writeText(socialUrl());
      toast.success(t("cities_share_social_copied"));
    } catch {
      toast.error(t("cities_share_failed"));
    }
  };
  const shareTo = (net) => {
    setShareOpen(false);
    const u = encodeURIComponent(socialUrl());
    const text = encodeURIComponent(yearLabel ? t("cities_title", { n: rows.length || top, year: yearLabel }) : t("cities_title_nodate", { n: top }));
    window.open(net === "linkedin" ? `https://www.linkedin.com/sharing/share-offsite/?url=${u}` : `https://twitter.com/intent/tweet?url=${u}&text=${text}`,
      "_blank", "noopener,noreferrer,width=640,height=560");
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success(t("cities_share_copied"));
    } catch {
      toast.error(t("cities_share_failed"));
    }
  };

  const onHover = (p, e) => {
    if (!p) return setTip(null);
    const host = e.currentTarget.ownerSVGElement.parentElement.getBoundingClientRect();
    setTip({ row: p, x: e.clientX - host.left, y: e.clientY - host.top, w: host.width });
  };

  const exportCsv = () => {
    const head = ["rank", "rank_prev", "city_code", "city", "country", "nights_total", "nights_foreign", "yoy_nights_pct", "foreign_share_pct", "population", "population_year", "nights_per_resident", "bucket", "greater_city", "year", "source"];
    const lines = [head.join(",")].concat(rows.map((r) => [
      r.rank, r.rank_prev ?? "", r.city, `"${r.name.replace(/"/g, '""')}"`, r.country_iso3, r.nights_total, r.nights_foreign, r.yoy_nights_pct ?? "", r.foreign_share_pct,
      r.population, r.population_year, r.nights_per_resident, r.bucket, r.greater_city, frame.year, `EUROSTAT ${r.dataset_code} + urb_cpop1`].join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `pulse-eu-cities-${frame.year}-top${top}.csv`; a.click();
    URL.revokeObjectURL(a.href);
  };

  const exportPng = async () => {
    if (!svgRef.current || exporting) return;
    setExporting(true);
    try {
      await exportChartPng(svgRef.current, { ...chartOpts(yearLabel), fileName: `pulse-eu-cities-${yearLabel}-top${top}-${lang}.png` });
    } catch {
      toast.error(t("cities_export_png_failed"));
    } finally {
      setExporting(false);
    }
  };

  const chartOpts = (y) => ({
    title: t("cities_title", { n: hist?.frames?.find((f) => f.year === y)?.rows.length || top, year: y }),
    subtitle: t("cities_subtitle", { year: y }),
    legendTitle: t("cities_legend_npr"),
    bucketLabel: (k) => t(`bucket_${k}`),
    footer: t("cities_png_footer"),
    dm,
  });

  const exportGif = async () => {
    if (!svgRef.current || gifBusy || years.length < 2) return;
    const startYear = year;
    setPlaying(false); setTip(null); setGifBusy(true);
    const tid = toast.loading(t("cities_gif_progress", { i: 1, n: years.length }));
    try {
      await exportTimelineGif({
        years,
        prepareYear: (y) => new Promise((resolve) => { setYear(y); requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(resolve, 40))); }),
        getSvg: () => svgRef.current,
        optsFor: chartOpts,
        fileName: `pulse-eu-cities-${years[0]}-${years[years.length - 1]}-top${top}-${lang}.gif`,
        onProgress: (i, n) => toast.loading(t("cities_gif_progress", { i, n }), { id: tid }),
      });
      toast.success(t("cities_gif_done", { n: years.length, from: years[0], to: years[years.length - 1] }), { id: tid });
    } catch {
      toast.error(t("cities_gif_failed"), { id: tid });
    } finally {
      setYear(startYear);
      setGifBusy(false);
    }
  };

  const exportMp4 = async () => {
    if (mp4Busy || years.length < 2 || !hist?.frames?.length) return;
    if (!videoExportSupported()) { toast.error(t("cities_mp4_unsupported")); return; }
    setPlaying(false); setTip(null); setMp4Busy(true);
    const tid = toast.loading(t("cities_mp4_progress", { pct: 0 }));
    try {
      const res = await exportTimelineVideo({
        frames: hist.frames, highlightIt, marked: cmp,
        strings: {
          title: (y) => t("cities_title", { n: hist.frames.find((f) => f.year === y)?.rows.length || top, year: y }),
          subtitle: (y) => t("cities_subtitle", { year: y }),
          x: (y) => t("cities_x", { year: y }), y: t("cities_y"),
          legend: t("cities_legend_npr"),
          buckets: Object.fromEntries(["over_50", "15_50", "5_15", "2_5", "under_2"].map((k) => [k, t(`bucket_${k}`)])),
          footer: t("cities_png_footer"),
        },
        fileBase: `pulse-eu-cities-timeline-${years[0]}-${years[years.length - 1]}-top${top}-${lang}`,
        onProgress: (p) => toast.loading(t("cities_mp4_progress", { pct: Math.round(p * 100) }), { id: tid }),
      });
      toast.success(t("cities_mp4_done", { sec: res.seconds, ext: res.ext.toUpperCase() }), { id: tid, duration: 8000 });
      if (res.ext === "webm") toast.message(t("cities_mp4_webm"), { duration: 8000 });
    } catch (e) {
      toast.error(e?.message === "webcodecs-unavailable" || e?.message === "no-encoder" ? t("cities_mp4_unsupported") : t("cities_gif_failed"), { id: tid });
    } finally {
      setMp4Busy(false);
    }
  };

  const sel = "text-xs px-2 py-1.5 outline-none";
  const selStyle = { background: T.surface, color: T.text, border: `1px solid ${T.border}`, borderRadius: 2 };
  const btn = "inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 disabled:opacity-40";
  const btnStyle = { background: T.surface, color: T.text, border: `1px solid ${T.border}`, borderRadius: 2 };

  return (
    <PulseLayout active="cities">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-8" data-testid="cities-page">
        <header className="mb-6">
          <h1 className="pulse-serif text-2xl md:text-3xl font-bold" style={{ color: T.text }} data-testid="cities-title">
            {yearLabel ? t("cities_title", { n: rows.length || top, year: yearLabel }) : t("cities_title_nodate", { n: top })}
          </h1>
          <p className="text-sm md:text-base mt-2 max-w-3xl" style={{ color: T.muted }}>
            {yearLabel ? t("cities_subtitle", { year: yearLabel }) : t("cities_subtitle_nodate")}
          </p>
        </header>

        <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="cities-controls">
          <label className="flex items-center gap-2 text-xs" style={{ color: T.muted }}>{t("cities_year")}
            <select className={sel} style={selStyle} value={year || ""} onChange={(e) => { setPlaying(false); setYear(e.target.value); }} data-testid="cities-year-select" disabled={!years.length}>
              {years.slice().reverse().map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>
          <label className="flex items-center gap-2 text-xs" style={{ color: T.muted }}>{t("cities_top")}
            <select className={sel} style={selStyle} value={top} onChange={(e) => setTop(parseInt(e.target.value, 10))} data-testid="cities-top-select">
              {[20, 30, 50].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <button type="button" onClick={() => setHighlightIt((v) => !v)} aria-pressed={highlightIt} data-testid="cities-highlight-toggle"
            className="text-xs font-semibold px-3 py-1.5 transition-colors"
            style={{ background: highlightIt ? T.brandSoft : T.surface, color: highlightIt ? T.accent : T.muted, border: `1px solid ${highlightIt ? T.accent : T.border}`, borderRadius: 2 }}>
            {t("cities_highlight_it")}
          </button>
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <button type="button" onClick={copyLink} data-testid="cities-share-link" className={btn} style={btnStyle}>
              <Link2 className="w-3.5 h-3.5" aria-hidden="true" /> {t("cities_share")}
            </button>
            <div className="relative">
              <button type="button" onClick={() => setShareOpen((o) => !o)} aria-haspopup="menu" aria-expanded={shareOpen} data-testid="cities-share-social" className={btn} style={btnStyle}>
                <Share2 className="w-3.5 h-3.5" aria-hidden="true" /> {t("cities_share_social")}
              </button>
              {shareOpen && (
                <div role="menu" className="absolute right-0 mt-1 z-30 min-w-[260px] shadow-lg py-1" data-testid="cities-share-menu"
                  style={{ background: T.surface, border: `1px solid ${T.border}` }} onMouseLeave={() => setShareOpen(false)}>
                  {[["copy", t("cities_share_social_copy"), copySocial], ["linkedin", t("cities_share_linkedin"), () => shareTo("linkedin")], ["x", t("cities_share_x"), () => shareTo("x")]].map(([k, label, fn]) => (
                    <button key={k} type="button" role="menuitem" onClick={fn} data-testid={`cities-share-${k === "copy" ? "copy-social" : k}`}
                      className="w-full text-left text-xs px-3 py-2 hover:opacity-80" style={{ color: T.text }}>{label}</button>
                  ))}
                </div>
              )}
            </div>
            {!narrow && (
              <button type="button" onClick={exportPng} disabled={!rows.length || exporting} data-testid="cities-export-png" className={btn} style={btnStyle}>
                <ImageIcon className="w-3.5 h-3.5" aria-hidden="true" /> {t("cities_export_png")}
              </button>
            )}
            {!narrow && years.length > 1 && (
              <button type="button" onClick={exportGif} disabled={!rows.length || gifBusy} data-testid="cities-export-gif" className={btn} style={btnStyle} aria-busy={gifBusy}>
                <Clapperboard className="w-3.5 h-3.5" aria-hidden="true" /> {t("cities_export_gif")}
              </button>
            )}
            {!narrow && years.length > 1 && (
              <button type="button" onClick={exportMp4} disabled={!rows.length || mp4Busy} data-testid="cities-export-mp4" className={btn} style={btnStyle} aria-busy={mp4Busy}>
                <Film className="w-3.5 h-3.5" aria-hidden="true" /> {t("cities_export_mp4")}
              </button>
            )}
            <button type="button" onClick={exportCsv} disabled={!rows.length} data-testid="cities-export-csv" className={btn} style={btnStyle}>
              <Download className="w-3.5 h-3.5" aria-hidden="true" /> {t("cities_export")}
            </button>
          </div>
        </div>

        {years.length > 1 && (
          <Timeline years={years} year={year} playing={playing} onPlay={onPlay} dm={dm} t={t} top={top}
            onYear={(y) => { setPlaying(false); setYear(y); }} />
        )}

        {years.length > 1 && missing.length === 0 && <div className="mb-4" style={{ minHeight: 36 }} aria-hidden="true" />}
        {missing.length > 0 && (
          <p className="text-[11px] leading-relaxed px-3 py-2 mb-4" data-testid="cities-partial-note"
            style={{ minHeight: 36, background: dm ? "rgba(245,158,11,0.10)" : "rgba(180,83,9,0.07)", color: dm ? "#FCD34D" : "#92400E", border: `1px solid ${dm ? "rgba(245,158,11,0.35)" : "rgba(180,83,9,0.25)"}` }}>
            {t("cities_partial", { year: frame.year, n: missing.length, top, latest: hist.latest, cities: missing.map((c) => c.name).join(", ") })}
          </p>
        )}

        <section className="relative p-3 md:p-5 mb-4" style={{ background: T.surface, border: `1px solid ${T.border}` }} onMouseLeave={() => setTip(null)}>
          {loading && !hist ? <SkeletonCard h={480} dm={dm} />
            : !rows.length ? <NoData msg={error === 424 || !years.length ? t("cities_empty") : t("no_data")} dm={dm} h={200} testid="cities-empty" />
            : narrow ? <CityRankedBars rows={rows} highlightIt={highlightIt} dm={dm} />
            : <CityBubbleChart rows={rows} domainRows={domainRows} year={frame.year} highlightIt={highlightIt} dm={dm} onHover={onHover} onSelect={onSelect} marked={cmp} svgRef={svgRef} />}
          {!narrow && <CityTooltip tip={tip} dm={dm} />}
          {!narrow && rows.length > 0 && <p className="text-[10px] mt-2 px-1" style={{ color: T.muted }} data-testid="cities-click-hint">{t("cities_click_hint")}</p>}
        </section>

        {frame?.sources?.length > 0 && (
          <div className="flex flex-col gap-1.5 mb-3" data-testid="cities-sources">
            {frame.sources.map((s) => <SourceChip key={s.dataset_code} source={s.source_org} dataset={s.dataset_code} period={s.period} dm={dm} testid={`cities-source-${s.dataset_code}`} />)}
          </div>
        )}
        {frame?.year && (
          <p className="text-[11px] leading-relaxed max-w-3xl mb-10" style={{ color: T.muted }} data-testid="cities-note">
            {t("cities_note", { year: frame.year, excluded, universe: frame.universe })}
          </p>
        )}

        {rows.length > 0 && (
          <section className="mb-10">
            <CityTable rows={rows} caption={t("cities_table_caption")} testid="cities-table" dm={dm} />
          </section>
        )}

        {italy.length > 0 && (
          <section className="mb-16" data-testid="cities-italy">
            <h2 className="pulse-serif text-lg font-semibold mb-1" style={{ color: T.text }}>{t("cities_italy_title")}</h2>
            <p className="text-xs mb-3" style={{ color: T.muted }}>{t("cities_italy_desc", { universe: frame.universe })}</p>
            <CityTable rows={italy} caption={t("cities_italy_title")} testid="cities-italy-table" dm={dm} />
          </section>
        )}

        {years.length > 0 && (
          <CityComparePanel a={cmp.a} b={cmp.b} onChange={(a, b) => setCmp({ a, b })} catalog={catalog} dm={dm} since={years[0]} />
        )}
      </div>
    </PulseLayout>
  );
}
